import React from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { INSERT_INLINE_COMMENT_COMMAND } from '../../CommentPlugin/CommentPlugin'
import { EXPLAIN_RESULT_COMMAND } from '../../ResultExplainerPlugin/ResultExplainerPlugin'
import { Button } from '../../../../../../../../../components/Button/Button'
import { ADD_TO_NOTEPAD_COMMAND } from '../../NotepadPlugin/NotepadPlugin'
import './DefaultFloatingToolbarContent.scss'
import { MissingScopeAction } from '../../../../../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../../../../../components/ScopedBlock/ScopedBlock'
import { AiFeature } from '../../../../../../../../../models/AiFeature'
import { AiResult } from '../../../../../../../../../models/AiResult'
import { TrackEvent, useTracking } from '../../../../../../common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../../../../../tracking'

export interface DefaultFloatingToolbarContentProps {
  aiFeature: AiFeature
  aiResult: AiResult
  withoutNotepad?: boolean
  withoutComments?: boolean
  withoutResultExplainer?: boolean
}

export const DefaultFloatingToolbarContent: React.FC<DefaultFloatingToolbarContentProps> = ({
  aiFeature,
  aiResult,
  withoutNotepad = false,
  withoutComments = false,
  withoutResultExplainer = false,
}) => {
  const [editor] = useLexicalComposerContext()

  const { tracked } = useTracking()

  const addToNotepad = tracked(() => editor.dispatchCommand(ADD_TO_NOTEPAD_COMMAND, undefined), {
    eventName: TrackEvent.AI_RESULT_ADDED_TO_NOTEPAD,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  const insertComment = tracked(() => editor.dispatchCommand(INSERT_INLINE_COMMENT_COMMAND, undefined), {
    eventName: TrackEvent.AI_RESULT_COMMENTED,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  const explainResult = tracked(() => editor.dispatchCommand(EXPLAIN_RESULT_COMMAND, undefined), {
    eventName: TrackEvent.AI_RESULT_EXPLAINED,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  return (
    <>
      {!withoutNotepad ? (
        <ScopedBlock
          scope={{
            scope: 'meeting_note:update',
            superScope: ['meeting_note:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='borderless' onClick={addToNotepad} className='button-small zocks-p-0.75 floating-toolbar-button' aria-label='Send to Notepad'>
            <i className='bi bi-journal-text' />
            Send to Notepad
          </Button>
        </ScopedBlock>
      ) : null}
      {!withoutComments ? (
        <ScopedBlock
          scope={{
            scope: 'comment:create',
            superScope: ['comment:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='borderless' onClick={insertComment} className='button-small zocks-p-0.75 floating-toolbar-button' aria-label='Insert comment'>
            <i className='bi bi-chat-square-text' />
            Comment
          </Button>
        </ScopedBlock>
      ) : null}
      {!withoutResultExplainer ? (
        <ScopedBlock
          scope={{
            scope: 'aifeature:execute',
            superScope: ['aifeature:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='borderless' onClick={explainResult} className='button-small zocks-p-0.75 floating-toolbar-button' aria-label='Explain'>
            <i className='bi bi-chat-square-quote' />
            Explain
          </Button>
        </ScopedBlock>
      ) : null}
    </>
  )
}
